import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import { showDialog } from '@components/dialog';
import { NotesPanel } from 'client/courses/components/member-notes-list';
import dayjs from 'dayjs';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { INTAKE_Q1, INTAKE_Q2, INTAKE_Q3, intakeOptionLabel } from 'shared/aip-intake';
import { bustCache } from './dashboard-cache';
import { OpBillingCard } from './op-billing-card';
import { BillingTransferCard } from './billing-transfer-card';

type Detail = Awaited<ReturnType<typeof rpx.engagementRetention.getMemberDetail>>;
type BillingCategory = Detail['billingCategory'];
type YcbmRow = Awaited<ReturnType<typeof rpx.engagementRetention.getYcbmCalls>>['rows'][number];
type Note = Awaited<ReturnType<typeof rpx.memberNotes.list>>['notes'][number];

export function MemberDetailPanel(props: {
  memberUserId: UUID;
  membershipScope?: 'retention' | 'all';
  onClose: () => void;
  onDataMutated?: () => void;
}) {
  const [detail, setDetail] = useState<Detail | undefined>(undefined);
  const [ycbmCalls, setYcbmCalls] = useState<YcbmRow[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | undefined>(undefined);
  const [isSyncingYcbm, setIsSyncingYcbm] = useState(false);
  const [ycbmSyncMsg, setYcbmSyncMsg] = useState<string | undefined>(undefined);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  async function refresh() {
    setIsLoading(true);
    try {
      const [dResult, yResult, nResult] = await Promise.allSettled([
        rpx.engagementRetention.getMemberDetail({
          userId: props.memberUserId,
          membershipScope: props.membershipScope,
        }),
        rpx.engagementRetention.getYcbmCalls({
          userId: props.memberUserId,
          membershipScope: props.membershipScope,
        }),
        rpx.memberNotes.list({ userId: props.memberUserId }),
      ]);
      if (dResult.status === 'rejected') {
        showError(dResult.reason);
        return;
      }
      setDetail(dResult.value);
      setYcbmCalls(yResult.status === 'fulfilled' ? yResult.value.rows : []);
      setNotes(nResult.status === 'fulfilled' ? nResult.value.notes : []);
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshNotes() {
    try {
      const n = await rpx.memberNotes.list({ userId: props.memberUserId });
      setNotes(n.notes);
    } catch (err) {
      showError(err);
    }
  }

  async function refreshYcbm() {
    setIsSyncingYcbm(true);
    setYcbmSyncMsg(undefined);
    try {
      const result = await rpx.engagementRetention.refreshYcbmSync({ userId: props.memberUserId });
      if (result.skipped) {
        setYcbmSyncMsg('YCBM not configured (env vars missing)');
      } else if (result.ok) {
        setYcbmSyncMsg(`Pulled ${result.inserted} new bookings`);
      } else {
        setYcbmSyncMsg(result.error || 'Sync failed');
      }
      bustCache('memberSignals');
      await refresh();
    } catch (err) {
      showError(err);
    } finally {
      setIsSyncingYcbm(false);
    }
  }

  async function refreshOp() {
    setIsSyncing(true);
    setSyncError(undefined);
    try {
      const result = await rpx.engagementRetention.refreshOpSync({ userId: props.memberUserId });
      if (!result.ok) {
        setSyncError(result.error || 'Sync failed');
      }
      bustCache('memberSignals');
      await refresh();
    } catch (err) {
      showError(err);
    } finally {
      setIsSyncing(false);
    }
  }

  async function cancelSubscription(opts: {
    purchaseId: string;
    memberName: string;
    isInTrial: boolean;
    disableAccessAtPeriodEnd: boolean;
  }) {
    const accessLine = opts.isInTrial
      ? 'Trial ends with no charge. Access continues until trial end.'
      : opts.disableAccessAtPeriodEnd
        ? 'Billing stops at the end of the current period. Access continues until then.'
        : 'Billing stops at the end of the current period. Access continues indefinitely (this purchase is set to keep access after cancellation).';
    const dialogTitle = opts.isInTrial ? 'Cancel trial' : 'Cancel subscription';
    const dialogConfirmText = opts.isInTrial ? 'Cancel trial' : 'Cancel subscription';
    const dialogCancelText = opts.isInTrial ? 'Keep trial' : 'Keep subscription';
    const dialogVerb = opts.isInTrial
      ? 'Cancel the Stripe trial'
      : 'Cancel the Stripe subscription';
    const isConfirmed = await showDialog({
      mode: 'warn',
      title: dialogTitle,
      children: (
        <div class="flex flex-col gap-2 text-sm text-gray-700">
          <p>
            {dialogVerb} for <span class="font-medium">{opts.memberName}</span>?
          </p>
          <p class="text-gray-600">{accessLine}</p>
          <p class="text-gray-600">Ontraport will reflect the change on the next nightly sync.</p>
        </div>
      ),
      confirmButtonText: dialogConfirmText,
      cancelButtonText: dialogCancelText,
    });
    if (!isConfirmed) {
      return;
    }
    setIsCanceling(true);
    try {
      await rpx.subscriptionStatus.cancelPurchase({
        purchaseId: opts.purchaseId,
        courseId: AIP_COURSE_ID,
        reason: 'admin-cancel',
      });
      bustCache('memberSignals');
      await refresh();
      setCancelSuccess(true);
      props.onDataMutated?.();
    } catch (err) {
      showError(err);
    } finally {
      setIsCanceling(false);
    }
  }

  useEffect(() => {
    setCancelSuccess(false);
    refresh();
  }, [props.memberUserId]);

  return createPortal(
    <div class="fixed inset-0 z-[1000] flex justify-end">
      <div class="absolute inset-0 bg-black/30" onClick={props.onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Member detail"
        class="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-xl"
      >
        <header class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 class="text-base font-semibold text-gray-900">Member detail</h2>
          <button
            type="button"
            class="text-sm text-gray-500 hover:text-gray-800"
            onClick={props.onClose}
          >
            Close
          </button>
        </header>

        {isLoading || !detail ? (
          <div class="p-5">
            <LoadingIndicator />
          </div>
        ) : (
          <div class="flex flex-col gap-5 p-5">
            <Section title="Profile">
              <p class="text-base font-medium text-gray-900">
                {detail.member.name || detail.member.email}
              </p>
              <p class="text-sm text-gray-500">{detail.member.email}</p>
              <div class="mt-2">
                <CategoryChip category={detail.billingCategory} mrrCents={detail.member.mrrCents} />
              </div>
              <p class="mt-2 text-sm text-gray-700">
                Joined {dayjs(detail.member.membershipCreatedAt).format('MMM D, YYYY')} ·{' '}
                {detail.member.tenureDays} days as a member
              </p>
              {detail.member.mrrCents !== undefined && (
                <p class="text-sm text-gray-700">${(detail.member.mrrCents / 100).toFixed(0)}/mo</p>
              )}
            </Section>

            <Section title="Engagement">
              <p class="text-sm text-gray-700">
                Calls attended: <span class="font-medium">{detail.member.callsAttended}</span>
                {detail.member.lastCallAttendedAt && (
                  <> · last {dayjs(detail.member.lastCallAttendedAt).fromNow()}</>
                )}
              </p>
              <p class="text-sm text-gray-700">
                Posts (90d): <span class="font-medium">{detail.member.postsCount}</span> · comments:{' '}
                <span class="font-medium">{detail.member.commentsCount}</span>
              </p>
              {detail.member.lastActiveAt && (
                <p class="text-sm text-gray-700">
                  Last active {dayjs(detail.member.lastActiveAt).fromNow()}
                </p>
              )}
            </Section>

            {detail.intake && (
              <Section title="Intake answers">
                <IntakeAnswers intake={detail.intake} />
              </Section>
            )}

            <Section title="Status">
              {cancelSuccess && (
                <div
                  role="status"
                  class="mb-2 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800"
                >
                  <p class="font-medium">Cancellation scheduled.</p>
                  <p class="mt-1">
                    {detail.member.aipCancelAt
                      ? `Access ends ${dayjs(detail.member.aipCancelAt).format('MMM D, YYYY')}.`
                      : 'Access ends at the end of the current billing period.'}{' '}
                    Ontraport will reflect this overnight.
                  </p>
                </div>
              )}
              <p class="text-sm text-gray-700">
                {detail.member.cancellationStatus === 'active' && 'Active subscription.'}
                {detail.member.cancellationStatus === 'paused' && (
                  <>
                    Paused
                    {detail.member.pausedUntil && (
                      <> until {dayjs(detail.member.pausedUntil).format('MMM D, YYYY')}</>
                    )}
                  </>
                )}
                {detail.member.cancellationStatus === 'canceling' && (
                  <>
                    Canceling
                    {detail.member.aipCancelAt ? (
                      <> — access ends {dayjs(detail.member.aipCancelAt).format('MMM D, YYYY')}</>
                    ) : (
                      <> — access ends at end of current period</>
                    )}
                    .
                  </>
                )}
                {detail.member.cancellationStatus === 'canceled' && (
                  <>
                    Canceled
                    {detail.member.cancelledAt && (
                      <> {dayjs(detail.member.cancelledAt).fromNow()}</>
                    )}
                  </>
                )}
                {detail.member.cancellationStatus === 'unknown' && 'Unknown subscription state.'}
              </p>
              {detail.member.cancellationReason && (
                <p class="text-sm text-gray-600">Reason: {detail.member.cancellationReason}</p>
              )}
              {detail.member.cancellationNotes && (
                <p class="text-sm text-gray-600 italic">"{detail.member.cancellationNotes}"</p>
              )}
              <PrimaryActionForCategory
                category={detail.billingCategory}
                memberName={detail.member.name || detail.member.email}
                trialDaysLeft={detail.member.trialDaysLeft}
                mrrCents={detail.member.mrrCents}
                disableAccessWhenCanceled={detail.member.disableAccessWhenCanceled}
                isCanceling={isCanceling}
                onCancel={cancelSubscription}
              />
            </Section>

            <OpBillingCard userId={props.memberUserId} />

            {detail.billingCategory.kind === 'op_billed' && (
              <BillingTransferCard userId={props.memberUserId} />
            )}

            <Section title="Ontraport engagement">
              <div class="flex items-baseline justify-between">
                <p class="text-xs text-gray-500">
                  {detail.member.opSyncedAt
                    ? `Synced ${dayjs(detail.member.opSyncedAt).fromNow()}`
                    : 'Never synced'}
                </p>
                <button
                  type="button"
                  class="text-xs text-indigo-700 hover:underline disabled:opacity-50"
                  onClick={refreshOp}
                  disabled={isSyncing}
                >
                  {isSyncing ? 'Syncing…' : 'Refresh from OP'}
                </button>
              </div>
              {syncError && <p class="mt-1 text-xs text-red-700">{syncError}</p>}
              {detail.member.opLastCampaignName && (
                <p class="text-sm text-gray-700">
                  Last campaign: <span class="font-medium">{detail.member.opLastCampaignName}</span>
                </p>
              )}
              {detail.member.opLastLeadSourceName && (
                <p class="text-sm text-gray-700">
                  Last lead source:{' '}
                  <span class="font-medium">{detail.member.opLastLeadSourceName}</span>
                </p>
              )}
              {detail.member.opDateLastEmailReceived && (
                <p class="text-sm text-gray-700">
                  Last email from us {dayjs(detail.member.opDateLastEmailReceived).fromNow()}
                </p>
              )}
              {detail.member.opDateLastCallLogged && (
                <p class="text-sm text-gray-700">
                  Last call logged {dayjs(detail.member.opDateLastCallLogged).fromNow()}
                </p>
              )}
              {detail.member.opVisitCount !== undefined && detail.member.opVisitCount > 0 && (
                <p class="text-sm text-gray-700">
                  Site visits: <span class="font-medium">{detail.member.opVisitCount}</span>
                </p>
              )}
              {detail.member.opTagNames && detail.member.opTagNames.length > 0 && (
                <div class="mt-2 flex flex-wrap gap-1">
                  {detail.member.opTagNames.map((tag) => (
                    <span key={tag} class="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Recent Mirasee calls (120d)">
              <div class="flex items-baseline justify-between">
                <p class="text-xs text-gray-500">
                  {detail.member.ycbmCallsCount}{' '}
                  {detail.member.ycbmCallsCount === 1 ? 'call' : 'calls'}
                  {detail.member.ycbmLastBookedAt && (
                    <> · last {dayjs(detail.member.ycbmLastBookedAt).fromNow()}</>
                  )}
                </p>
                <button
                  type="button"
                  class="text-xs text-indigo-700 hover:underline disabled:opacity-50"
                  onClick={refreshYcbm}
                  disabled={isSyncingYcbm}
                >
                  {isSyncingYcbm ? 'Syncing…' : 'Sync from YCBM'}
                </button>
              </div>
              {ycbmSyncMsg && <p class="mt-1 text-xs text-gray-600">{ycbmSyncMsg}</p>}
              {ycbmCalls.length > 0 && (
                <ul class="mt-2 flex flex-col gap-1 text-sm">
                  {ycbmCalls.map((c) => (
                    <li key={c.id} class="flex items-baseline justify-between">
                      <span class="text-gray-700">
                        {dayjs(c.bookedAt).format('MMM D')}
                        {c.appointmentType ? ` · ${c.appointmentType}` : ''}
                        {c.durationMin ? ` · ${c.durationMin} min` : ''}
                      </span>
                      <span class="text-xs text-gray-500">{c.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Notes">
              <NotesPanel
                memberUserId={props.memberUserId}
                notes={notes}
                isLoading={false}
                onChange={refreshNotes}
              />
            </Section>

            <Section title="Outreach log">
              {detail.outreach.length === 0 ? (
                <p class="text-sm text-gray-500">No outreach logged yet.</p>
              ) : (
                <ul class="flex flex-col gap-2">
                  {detail.outreach.map((row) => (
                    <li key={row.id} class="rounded border border-gray-200 bg-gray-50 p-2 text-sm">
                      <div class="flex items-baseline justify-between">
                        <span class="text-xs font-medium text-gray-700">
                          {row.actionType.replace(/_/g, ' ')}
                          {row.outcome ? ` · ${row.outcome.replace(/_/g, ' ')}` : ''}
                        </span>
                        <span class="text-xs text-gray-500">
                          {dayjs(row.createdAt).format('MMM D')}
                          {row.actorName ? ` · ${row.actorName}` : ''}
                        </span>
                      </div>
                      {row.body && <p class="mt-1 text-sm text-gray-700">{row.body}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function IntakeAnswers({ intake }: { intake: NonNullable<Detail['intake']> }) {
  if (!intake.completedAt) {
    return (
      <p class="text-sm text-gray-700">
        Not answered yet · shown {intake.shownCount} {intake.shownCount === 1 ? 'time' : 'times'}
        {intake.skippedAt && <> · last skipped {dayjs(intake.skippedAt).fromNow()}</>}
      </p>
    );
  }
  const q1 =
    intake.q1 === 'other'
      ? `Other: “${intake.q1Other ?? ''}”`
      : intakeOptionLabel(INTAKE_Q1, intake.q1 ?? '');
  return (
    <div class="flex flex-col gap-2 text-sm text-gray-700">
      <p class="text-gray-500">
        Answered {dayjs(intake.firstCompletedAt ?? intake.completedAt).format('MMM D, YYYY')}
        {intake.pickSource === 'fallback' && ' · standard picks (Claude unavailable)'}
      </p>
      <dl class="flex flex-col gap-1.5">
        <dt class="font-medium">{INTAKE_Q1.text}</dt>
        <dd>{q1}</dd>
        <dt class="font-medium">{INTAKE_Q2.text}</dt>
        <dd>{(intake.q2 ?? []).map((id) => intakeOptionLabel(INTAKE_Q2, id)).join('; ')}</dd>
        <dt class="font-medium">{INTAKE_Q3.text}</dt>
        <dd>{intakeOptionLabel(INTAKE_Q3, intake.q3 ?? '')}</dd>
      </dl>
      {intake.picks.length > 0 && (
        <>
          <p class="font-medium">Recordings picked</p>
          <ul class="flex flex-col gap-1">
            {intake.picks.map((p, i) => (
              <li key={i}>
                <span class="font-medium">{p.title}:</span> {p.reason}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Section(props: { title: string; children: preact.ComponentChildren }) {
  return (
    <section>
      <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {props.title}
      </h3>
      <div class="flex flex-col gap-1">{props.children}</div>
    </section>
  );
}

function CategoryChip(props: { category: BillingCategory; mrrCents?: number }) {
  const c = props.category;
  let label = '';
  let classes = '';
  switch (c.kind) {
    case 'app_billed':
      label =
        c.subKind === 'standalone'
          ? `App-billed${props.mrrCents ? ` · $${(props.mrrCents / 100).toFixed(0)}/mo` : ''}`
          : `App-billed (cohort bundle)${props.mrrCents ? ` · $${(props.mrrCents / 100).toFixed(0)}/mo` : ''}`;
      classes = 'bg-blue-50 text-blue-800 border border-blue-200';
      break;
    case 'op_billed':
      label =
        c.opStatus === 'cancelled'
          ? 'Legacy · Ontraport (cancelled there)'
          : c.opStatus === 'free_trial'
            ? 'Legacy · Ontraport (free trial)'
            : c.opStatus === 'pending'
              ? 'Legacy · Ontraport (pending)'
              : 'Legacy · Ontraport-billed';
      classes =
        c.opStatus === 'cancelled'
          ? 'bg-purple-50/60 text-purple-600 border border-purple-100'
          : 'bg-purple-50 text-purple-800 border border-purple-200';
      break;
    case 'stripe_trial':
      label =
        c.trialDaysLeft === 1
          ? 'Trial · ends within 24 hours'
          : `Trial · ${c.trialDaysLeft} days left`;
      classes = 'bg-amber-50 text-amber-800 border border-amber-200';
      break;
    case 'manual_comp':
      label = 'Manual grant · no billing';
      classes = 'bg-gray-100 text-gray-700 border border-gray-200';
      break;
    case 'canceling':
      label = 'Canceling';
      classes = 'bg-orange-50 text-orange-800 border border-orange-200';
      break;
    case 'canceled':
      label = 'Canceled';
      classes = 'bg-gray-100 text-gray-600 border border-gray-200';
      break;
    case 'unknown':
      label = 'Billing unknown — verify in Ontraport';
      classes = 'bg-yellow-50 text-yellow-800 border border-yellow-200';
      break;
  }
  return (
    <span
      class={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classes}`}
    >
      {label}
    </span>
  );
}

function PrimaryActionForCategory(props: {
  category: BillingCategory;
  memberName: string;
  trialDaysLeft?: number;
  mrrCents?: number;
  disableAccessWhenCanceled?: boolean;
  isCanceling: boolean;
  onCancel: (opts: {
    purchaseId: string;
    memberName: string;
    isInTrial: boolean;
    disableAccessAtPeriodEnd: boolean;
  }) => void;
}) {
  const c = props.category;
  const disableAccessAtPeriodEnd = props.disableAccessWhenCanceled !== false;

  if (c.kind === 'app_billed') {
    const note =
      c.subKind === 'cohort_bundle' ? (
        <p class="mt-2 text-xs text-gray-600">
          Heads-up: this is a cohort-bundle subscription. Canceling stops billing for the entire
          bundle (including cohort access), not just AIP.
        </p>
      ) : undefined;
    return (
      <div class="mt-3">
        <button
          type="button"
          class="rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          onClick={() =>
            props.onCancel({
              purchaseId: c.purchaseId,
              memberName: props.memberName,
              isInTrial: false,
              disableAccessAtPeriodEnd,
            })
          }
          disabled={props.isCanceling}
        >
          {props.isCanceling ? 'Canceling…' : 'Cancel subscription'}
        </button>
        {note}
      </div>
    );
  }

  if (c.kind === 'stripe_trial') {
    const mrrLabel = props.mrrCents
      ? `$${(props.mrrCents / 100).toFixed(0)}/mo`
      : 'the monthly rate';
    return (
      <div class="mt-3 flex flex-col gap-2">
        <p class="text-xs text-gray-600">
          Trial — Stripe will auto-charge {mrrLabel} when the trial ends{' '}
          {c.trialDaysLeft === 1 ? 'within 24 hours' : `in ${c.trialDaysLeft} days`}. Cancel before
          then to prevent the charge.
        </p>
        <button
          type="button"
          class="self-start rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          onClick={() =>
            props.onCancel({
              purchaseId: c.purchaseId,
              memberName: props.memberName,
              isInTrial: true,
              disableAccessAtPeriodEnd,
            })
          }
          disabled={props.isCanceling}
        >
          {props.isCanceling ? 'Canceling…' : 'Cancel trial'}
        </button>
      </div>
    );
  }

  if (c.kind === 'op_billed') {
    const copy =
      c.opStatus === 'cancelled'
        ? 'Previously billed in Ontraport (now cancelled there). Access is currently granted in our app — review whether to revoke.'
        : 'Billing managed in Ontraport. Our app has no levers for this membership — cancel from the OP contact.';
    return (
      <div class="mt-3 flex flex-col gap-2">
        <p class="text-xs text-gray-600">{copy}</p>
        {c.opContactUrl && (
          <a
            href={c.opContactUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="self-start rounded border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
          >
            Open in Ontraport →
          </a>
        )}
      </div>
    );
  }

  if (c.kind === 'manual_comp') {
    return <p class="mt-3 text-xs text-gray-600">Granted manually — no billing to cancel.</p>;
  }

  if (c.kind === 'unknown') {
    const reasonCopy =
      c.reason === 'op_not_configured'
        ? 'Ontraport is not configured for this tenant.'
        : c.reason === 'no_profile'
          ? 'No user profile row found for this member.'
          : 'Could not reach Ontraport to check billing state.';
    return (
      <p class="mt-3 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-900">
        {reasonCopy} Verify in OP before acting on this member.
      </p>
    );
  }

  if (c.kind === 'canceling' || c.kind === 'canceled') {
    return undefined;
  }

  return undefined;
}
